import { t } from 'i18next';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { montserratVfs } from 'assets/fonts/montserratVfs';
import { AnswerSubfield, Field, Form, Item } from 'store/states/forms/types';
import { Historic, HistoricAnswer } from 'store/states/historics/types';
import { getBase64FromImageUrl, getImageDimensions } from './download-image';
import { collapseLineBreaks } from './collapseLineBreaks';
import { formatTimestampDate } from './formatters';
import { verifyRules } from './verifyRules';
import { logoBooster } from './logoBase64';
import {
  buildCombinedPdfName,
  buildIndividualPdfName,
  ensurePdfExtension,
} from './pdfFileName';

// pdfmake >=0.2.7 empacota o vfs como `module.exports = vfs`, mas o interop do
// bundler pode embrulhar em { default: vfs }. Normalizamos aqui pra evitar o
// erro "Roboto-Medium.ttf not found in virtual file system". Comportamento de
// fonte segue o mesmo (Roboto padrao do pdfmake) — so a resolucao do objeto.
const pdfFontsResolved = pdfFonts as any;
const PDF_VFS =
  pdfFontsResolved?.pdfMake?.vfs ??
  pdfFontsResolved?.vfs ??
  pdfFontsResolved?.default?.pdfMake?.vfs ??
  pdfFontsResolved?.default?.vfs ??
  pdfFontsResolved?.default ??
  pdfFontsResolved;

// Registra o vfs (Roboto do pdfmake + Montserrat) e o mapeamento da fonte
// Montserrat. Paridade com o app mobile: Regular = normal, Bold = bold; italico
// mapeado pros mesmos pesos (o PDF nao usa italico).
function setupPdfFonts() {
  const allVfs = {
    ...(PDF_VFS as { [file: string]: string }),
    ...montserratVfs,
  };
  const pdf = pdfMake as any;
  // pdfmake 0.2.x usa um vfs interno via addVirtualFileSystem; setar .vfs direto
  // pode ser ignorado quando esse metodo existe. Usamos ele com fallback.
  if (typeof pdf.addVirtualFileSystem === 'function') {
    pdf.addVirtualFileSystem(allVfs);
  } else {
    pdf.vfs = allVfs;
  }
  pdf.fonts = {
    Montserrat: {
      normal: 'Montserrat-Regular.ttf',
      bold: 'Montserrat-Bold.ttf',
      italics: 'Montserrat-Regular.ttf',
      bolditalics: 'Montserrat-Bold.ttf',
    },
  };
}

const PAGE_MARGIN = 32;
const A4_CONTENT_WIDTH = 595 - PAGE_MARGIN * 2; // 531
const A4_CONTENT_HEIGHT = 842 - PAGE_MARGIN * 2; // 778
// ~36% da página útil: garante título + 1 linha de fotos num bloco atômico
// que sempre cabe numa página (AGROS-3259)
const IMAGE_BOX_HEIGHT = 280;
const LETTERBOX_BG = '#F6F6F6';
const LETTERBOX_PADDING = 6;
const CELL_MARGIN_RIGHT = 12;

// Chunk de 800 chars na largura total ocupa <1/3 de página em qualquer
// fonte usada (12-18pt), então nunca estoura a sobra de uma página
const TEXT_CHUNK_MAX = 800;
const CHECKBOX_CELL_MAX = 600;
const TITLE_MAX = 1500;

function calcFontSizes(columnCount: number) {
  const bodyFontSize = Math.min(Math.max(18 - (columnCount - 1) * 2, 12), 18);
  const titleFontSize = bodyFontSize * 0.85;
  return { bodyFontSize, titleFontSize };
}

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) : text;
}

// Capacidade estimada de uma célula do grid antes de estourar a página:
// uma linha de tabela com dontBreakRows é indivisível, então texto acima
// desse limite sai do grid e vira seção full-width em chunks
function calcGridTextLimit(colWidth: number, bodyFontSize: number): number {
  const charsPerLine = Math.floor(
    (colWidth - CELL_MARGIN_RIGHT) / (bodyFontSize * 0.5),
  );
  const linesPerPage = Math.floor(A4_CONTENT_HEIGHT / (bodyFontSize * 1.2));
  return Math.min(1000, Math.floor(charsPerLine * linesPerPage * 0.8));
}

// Texto com muitas quebras de linha (\n) ocupa altura mesmo com poucos
// caracteres: cada linha explicita consome ao menos uma linha visual. Conta as
// linhas visuais estimadas e compara com a capacidade da celula (AGROS-3495).
export function exceedsGridCapacity(
  text: string,
  colWidth: number,
  bodyFontSize: number,
): boolean {
  if (text.length > calcGridTextLimit(colWidth, bodyFontSize)) return true;
  const charsPerLine = Math.max(
    1,
    Math.floor((colWidth - CELL_MARGIN_RIGHT) / (bodyFontSize * 0.5)),
  );
  const linesPerPage = Math.floor(A4_CONTENT_HEIGHT / (bodyFontSize * 1.2));
  const visualLines = text
    .split('\n')
    .reduce(
      (total, line) => total + Math.max(1, Math.ceil(line.length / charsPerLine)),
      0,
    );
  return visualLines > Math.floor(linesPerPage * 0.8);
}

export function splitIntoChunks(
  text: string,
  maxLen = TEXT_CHUNK_MAX,
): string[] {
  const chunks: string[] = [];
  const paragraphs = text
    .split('\n')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  paragraphs.forEach((paragraph) => {
    let rest = paragraph;
    while (rest.length > maxLen) {
      const window = rest.slice(0, maxLen);
      // Prefere fim de frase, depois espaço; fallback só vale na segunda
      // metade da janela (evita chunk minúsculo); senão corte duro
      const sentenceCut = window.lastIndexOf('. ');
      let cut: number;
      if (sentenceCut >= maxLen / 2) {
        cut = sentenceCut + 1;
      } else {
        const spaceCut = window.lastIndexOf(' ');
        cut = spaceCut >= maxLen / 2 ? spaceCut : maxLen;
      }
      chunks.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest.length > 0) chunks.push(rest);
  });
  return chunks;
}

function getLabelByValue(items?: Item[] | null, value?: string | null): string {
  return items?.find((it) => it.value === value)?.label ?? value ?? '';
}

type SimpleItem = {
  kind: 'simple';
  header: string;
  value: string;
};

type LoadedImage = {
  src: string;
  width: number;
  height: number;
};

type ImageItem = {
  kind: 'image';
  header: string;
  image: LoadedImage;
};

type MultiImageItem = {
  kind: 'multi_image';
  header: string;
  images: LoadedImage[];
};

type CheckboxGroupItem = {
  kind: 'checkbox_group';
  header: string;
  subfields: AnswerSubfield[];
};

export type PdfItem =
  | SimpleItem
  | ImageItem
  | MultiImageItem
  | CheckboxGroupItem;

function buildLetterboxCell(image: LoadedImage, cellWidth: number) {
  const contentW = cellWidth - 2 * LETTERBOX_PADDING;
  const contentH = IMAGE_BOX_HEIGHT - 2 * LETTERBOX_PADDING;
  const scale = Math.min(contentW / image.width, contentH / image.height);
  const finalW = image.width * scale;
  const finalH = image.height * scale;
  const vGap = Math.max(0, (contentH - finalH) / 2);
  return {
    image: image.src,
    width: finalW,
    height: finalH,
    alignment: 'center',
    margin: [
      LETTERBOX_PADDING,
      LETTERBOX_PADDING + vGap,
      LETTERBOX_PADDING,
      LETTERBOX_PADDING + vGap,
    ],
    fillColor: LETTERBOX_BG,
  };
}

// Anexo de documento chega como resposta de imagem, mas a URL aponta pra um
// application/pdf. pdfmake so embute png/jpeg, entao esses anexos sao pulados
// (fotos continuam entrando normalmente).
function isPdfUrl(url: string): boolean {
  return url.includes('response-content-type=application%2Fpdf');
}

// Aceita array real ou string JSON ("[url1,url2]") ou URL unica, igual a tela
// de historico (HistoricInputSelector.parseUrls), e descarta anexos PDF.
function parseAnswerUrls(
  answer: string | AnswerSubfield[] | null,
): string[] {
  let urls: string[] = [];
  if (Array.isArray(answer)) {
    urls = (answer as unknown as unknown[]).filter(
      (u): u is string => typeof u === 'string' && u.trim() !== '',
    );
  } else if (typeof answer === 'string') {
    const trimmed = answer.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr)) {
          urls = arr.filter(
            (u): u is string => typeof u === 'string' && u.trim() !== '',
          );
        }
      } catch {
        urls = trimmed ? [trimmed] : [];
      }
    } else {
      urls = trimmed ? [trimmed] : [];
    }
  }
  return urls.map((u) => u.trim()).filter((u) => !isPdfUrl(u));
}

async function resolveImageUrls(
  answer: HistoricAnswer,
): Promise<LoadedImage[]> {
  const urls = parseAnswerUrls(answer.answer);
  const dataUrls = (
    await Promise.all(urls.map(getBase64FromImageUrl))
  ).filter(Boolean);
  const loaded = await Promise.all(
    dataUrls.map(async (src) => {
      const dims = await getImageDimensions(src);
      return { src, width: dims.width, height: dims.height };
    }),
  );
  return loaded;
}

async function buildPdfItem(
  answer: HistoricAnswer | undefined,
  field: Field | undefined,
  formFields: Field[],
  locale: string,
  header: string,
): Promise<PdfItem | null> {
  if (answer && answer.component_type === 'checkbox_group') {
    let subfields: AnswerSubfield[] = [];
    if (answer.answer != null && answer.answer !== '') {
      try {
        subfields =
          typeof answer.answer === 'string'
            ? (JSON.parse(answer.answer) as AnswerSubfield[])
            : (answer.answer as AnswerSubfield[]);
      } catch {
        subfields = [];
      }
    }
    if (!subfields || subfields.length === 0) {
      return { kind: 'simple', header, value: t('not_informed') };
    }
    return { kind: 'checkbox_group', header, subfields };
  }

  if (
    answer === undefined ||
    answer?.answer === null ||
    answer?.answer === ''
  ) {
    return { kind: 'simple', header, value: t('not_informed') };
  }

  if (answer.component_type === 'date') {
    return {
      kind: 'simple',
      header,
      value:
        formatTimestampDate(answer.answer as string, locale) ||
        t('not_informed'),
    };
  }

  if (['image', 'draw', 'signature'].includes(answer.component_type)) {
    const images = await resolveImageUrls(answer);
    // Sem imagem renderizavel (ex: campo vazio ou apenas anexo PDF pulado):
    // nao deixa a celula vazia, mostra "Nao informado".
    if (images.length === 0) {
      return { kind: 'simple', header, value: t('not_informed') };
    }
    if (images.length === 1) return { kind: 'image', header, image: images[0] };
    return { kind: 'multi_image', header, images };
  }

  if (
    answer.component_type === 'checkbox' &&
    typeof answer.answer === 'string'
  ) {
    const allItems = answer.answer.split('\t').slice(1);
    const value = allItems
      .map(
        (itemValue) =>
          field?.items?.find((it) => it.value === itemValue)?.label ??
          itemValue,
      )
      .join(', ');
    return { kind: 'simple', header, value: value || t('not_informed') };
  }

  if (answer.component_type === 'radio' && typeof answer.answer === 'string') {
    const value =
      field?.items?.find((it) => it.value === answer.answer)?.label ??
      answer.answer;
    return { kind: 'simple', header, value: value || t('not_informed') };
  }

  if (
    answer.component_type === 'decimal' &&
    typeof answer.answer === 'string'
  ) {
    // Decimal usa virgula como separador (paridade com mobile/pt-BR).
    return {
      kind: 'simple',
      header,
      value: answer.answer.replace('.', ',') || t('not_informed'),
    };
  }

  return { kind: 'simple', header, value: `${answer.answer}` || t('not_informed') };
}

function getHeader(question: string, description?: string) {
  if (question !== description) {
    return truncate(
      `${question}
    ${description?.replace(/<\/?[^>]+(>|$)/g, '')}
    `,
      TITLE_MAX,
    );
  }
  return truncate(question, TITLE_MAX);
}

export async function formatHistoricToItems(
  historic: Historic,
  formFields: Field[],
  locale: string,
): Promise<PdfItem[]> {
  const rulesAnswers = historic.answers.map((a) => ({
    field_id: a.field_id,
    value: a.answer,
  }));

  const itemsPromises = formFields.map(async (field) => {
    if (!verifyRules(field, rulesAnswers)) return null;
    const answer = historic.answers.find((a) => a.field_id === field.field_id);
    const header = getHeader(
      answer?.question ?? field.description,
      field.description,
    );
    return buildPdfItem(answer, field, formFields, locale, header);
  });

  const items = (await Promise.all(itemsPromises)).filter(
    (i): i is PdfItem => i !== null,
  );

  const meta: SimpleItem[] = [
    { kind: 'simple', header: t('form_name'), value: historic.form_name ?? '' },
    { kind: 'simple', header: t('username'), value: historic.user_name ?? '' },
    {
      kind: 'simple',
      header: t('registration_date'),
      value: formatTimestampDate(historic.form_date, locale),
    },
  ];

  return [...meta, ...items];
}

function renderSimpleCell(
  item: SimpleItem | ImageItem | null,
  colWidth: number,
) {
  if (!item) return { text: '' };
  if (item.kind === 'image') {
    const innerWidth = colWidth - CELL_MARGIN_RIGHT;
    return {
      margin: [0, 0, 0, 16],
      table: {
        widths: [innerWidth],
        heights: [IMAGE_BOX_HEIGHT],
        body: [[buildLetterboxCell(item.image, innerWidth)]],
        dontBreakRows: true,
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    };
  }
  return { text: item.value || '', margin: [0, 0, 0, 16] };
}

function buildSimpleRowsTable(
  buffer: (SimpleItem | ImageItem)[],
  columnCount: number,
  colWidth: number,
) {
  const blocks: any[] = [];
  for (let i = 0; i < buffer.length; i += columnCount) {
    const chunk = buffer.slice(i, i + columnCount);
    const headerRow = chunk.map((c) => ({
      text: c.header,
      style: 'tableHeader',
      margin: [0, 0, 0, 4],
    }));
    const valueRow = chunk.map((c) => renderSimpleCell(c, colWidth));
    while (headerRow.length < columnCount) {
      headerRow.push({ text: '', style: 'tableHeader', margin: [0, 0, 0, 4] });
      valueRow.push({ text: '' } as any);
    }
    // Título (headerRow) + valor (valueRow) num bloco atômico: dontBreakRows só
    // impede uma linha de quebrar, mas ainda deixa o título no fim de uma página
    // e o valor na seguinte. unbreakable mantém o par junto (AGROS-3297).
    blocks.push({
      unbreakable: true,
      stack: [
        {
          style: 'table',
          color: '#444',
          table: {
            widths: Array(columnCount).fill(colWidth),
            body: [headerRow, valueRow],
            dontBreakRows: true,
          },
          layout: 'noBorders',
        },
      ],
    });
  }
  return blocks;
}

// Resposta de texto acima do limite do grid vira seção própria full-width:
// título + chunks independentes, o paginador quebra livremente entre eles
function buildLongTextBlock(item: SimpleItem) {
  const blocks: any[] = [
    {
      text: item.header,
      style: 'tableHeader',
      margin: [0, 0, 0, 4],
    },
  ];
  const chunks = splitIntoChunks(item.value);
  chunks.forEach((chunk, i) => {
    blocks.push({
      text: chunk,
      margin: [0, 0, 0, i === chunks.length - 1 ? 16 : 8],
    });
  });
  return blocks;
}

function buildMultiImageBlock(
  item: MultiImageItem,
  columnCount: number,
  colWidth: number,
) {
  const headerBlock = {
    text: item.header,
    style: 'tableHeader',
    margin: [0, 0, 0, 8],
  };

  const imageColWidth =
    (A4_CONTENT_WIDTH - (columnCount - 1) * CELL_MARGIN_RIGHT) / columnCount;
  const widths: any[] = [];
  for (let c = 0; c < columnCount; c += 1) {
    widths.push(imageColWidth);
    if (c < columnCount - 1) widths.push(CELL_MARGIN_RIGHT);
  }

  const rowBlocks: any[] = [];
  for (let i = 0; i < item.images.length; i += columnCount) {
    const chunk = item.images.slice(i, i + columnCount);
    const cells: any[] = [];
    for (let c = 0; c < columnCount; c += 1) {
      cells.push(
        chunk[c] ? buildLetterboxCell(chunk[c], imageColWidth) : { text: '' },
      );
      if (c < columnCount - 1) cells.push({ text: '' });
    }
    rowBlocks.push({
      margin: [0, 0, 0, 12],
      table: {
        widths,
        heights: [IMAGE_BOX_HEIGHT],
        body: [cells],
        dontBreakRows: true,
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    });
  }

  if (rowBlocks.length === 0) return [headerBlock];

  // Título + primeira linha de fotos num bloco atômico: não coube no fim
  // da página, vai inteiro pra próxima — evita título órfão (AGROS-3259)
  return [
    { stack: [headerBlock, rowBlocks[0]], unbreakable: true },
    ...rowBlocks.slice(1),
  ];
}

function buildCheckboxGroupBlock(
  item: CheckboxGroupItem,
  formFields: Field[],
  columnCount: number,
  colWidth: number,
  bodyFontSize: number,
) {
  const parentField = formFields.find((f) => f.type === 'checkbox_group');

  const blocks: any[] = [
    {
      text: item.header,
      style: 'tableHeader',
      margin: [0, 0, 0, 4],
    },
  ];

  for (let i = 0; i < item.subfields.length; i += columnCount) {
    const chunk = item.subfields.slice(i, i + columnCount);

    const headerRow = chunk.map((sf) => ({
      text: truncate(
        getLabelByValue(parentField?.items, sf.value).toUpperCase(),
        CHECKBOX_CELL_MAX,
      ),
      bold: true,
      fontSize: bodyFontSize * 0.85,
      margin: [0, 8, 0, 8],
    }));
    while (headerRow.length < columnCount) {
      headerRow.push({
        text: '',
        bold: true,
        fontSize: bodyFontSize * 0.85,
        margin: [0, 8, 0, 8],
      });
    }

    const maxSubVals = Math.max(
      ...chunk.map((sf) => sf.sub_fields?.length ?? 0),
      0,
    );

    const valueRows: any[] = [];
    for (let j = 0; j < maxSubVals; j += 1) {
      const isLast = j === maxSubVals - 1;
      const cells = chunk.map((sf) => {
        const subAnswer = sf.sub_fields?.[j];
        if (!subAnswer) {
          return { text: '', margin: [8, 0, 0, isLast ? 16 : 8] };
        }
        const subFieldDef = formFields
          .flatMap((f) => f.sub_fields ?? [])
          .find((f) => f.field_id === (subAnswer as any).field_id);
        const rawValue = (subAnswer as any).value;
        const hasValue =
          rawValue !== null && rawValue !== undefined && rawValue !== '';
        const subLabel = hasValue
          ? getLabelByValue(subFieldDef?.items, rawValue) || t('not_informed')
          : t('not_informed');
        return {
          stack: [
            {
              text: truncate(subFieldDef?.summary_title ?? '', CHECKBOX_CELL_MAX),
              bold: true,
              color: '#767676',
              margin: [0, 0, 0, 4],
            },
            {
              text: truncate(`${subLabel}`, CHECKBOX_CELL_MAX),
              bold: true,
              color: '#000000',
            },
          ],
          margin: [8, 0, 0, isLast ? 16 : 8],
        };
      });
      while (cells.length < columnCount) {
        cells.push({ text: '', margin: [8, 0, 0, isLast ? 16 : 8] } as any);
      }
      valueRows.push(cells);
    }

    blocks.push({
      table: {
        widths: Array(columnCount).fill(colWidth),
        body: [headerRow, ...valueRows],
        dontBreakRows: false,
      },
      layout: 'noBorders',
    });
  }
  return blocks;
}

// Paridade com o histórico: quebras de linha consecutivas viram uma só no
// texto das respostas e dos subcampos (AGROS-3495)
function collapseItemLineBreaks(item: PdfItem): PdfItem {
  if (item.kind === 'simple') {
    return { ...item, value: collapseLineBreaks(item.value) };
  }
  if (item.kind === 'checkbox_group') {
    return {
      ...item,
      subfields: item.subfields.map((parent) => ({
        ...parent,
        sub_fields: parent.sub_fields?.map((sub) => (typeof sub.value === 'string'
          ? { ...sub, value: collapseLineBreaks(sub.value) }
          : sub)),
      })),
    };
  }
  return item;
}

export function generatePdfJson(
  items: PdfItem[],
  formFields: Field[],
  columnCount: number,
  bodyFontSize: number,
) {
  const result: any[] = [];
  let buffer: (SimpleItem | ImageItem)[] = [];
  const colWidth = Math.floor(A4_CONTENT_WIDTH / columnCount);

  const flushSimples = () => {
    if (buffer.length === 0) return;
    result.push(...buildSimpleRowsTable(buffer, columnCount, colWidth));
    buffer = [];
  };

  items.map(collapseItemLineBreaks).forEach((item) => {
    if (
      item.kind === 'simple' &&
      exceedsGridCapacity(item.value, colWidth, bodyFontSize)
    ) {
      // Célula do grid é indivisível (dontBreakRows): texto acima da
      // capacidade de uma página sai do grid e flui em chunks full-width
      flushSimples();
      result.push(...buildLongTextBlock(item));
    } else if (item.kind === 'checkbox_group') {
      flushSimples();
      result.push(
        ...buildCheckboxGroupBlock(
          item,
          formFields,
          columnCount,
          colWidth,
          bodyFontSize,
        ),
      );
    } else if (item.kind === 'multi_image') {
      flushSimples();
      result.push(...buildMultiImageBlock(item, columnCount, colWidth));
    } else {
      buffer.push(item);
    }
  });
  flushSimples();

  return result;
}

function buildLogoHeader(customLogo: string | null) {
  return customLogo
    ? {
        columns: [
          { image: logoBooster, width: 150 },
          { text: '', width: '*' },
          { image: customLogo, width: 150 },
        ],
      }
    : { image: logoBooster, width: 150 };
}

// Nome da org: linha propria full-width, logo abaixo do cabecalho e antes dos
// metadados. So aparece quando a org tem nome (paridade com mobile).
function buildOrgNameBlock(orgName: string) {
  return {
    text: orgName,
    bold: true,
    fontSize: 16,
    margin: [0, 12, 0, 12],
  };
}

function buildDocDefinitions(
  content: any[],
  titleFontSize: number,
  bodyFontSize: number,
) {
  return {
    pageSize: 'A4',
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
    content,
    styles: {
      header: {
        fontSize: 14,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      subheader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 5],
      },
      table: {
        margin: [0, 0, 0, 8],
        border: 0,
      },
      tableHeader: {
        bold: true,
        fontSize: titleFontSize,
        color: 'black',
      },
    },
    defaultStyle: {
      font: 'Montserrat',
      fontSize: bodyFontSize,
    },
  };
}

// Monta a secao de UMA anotacao (cabecalho de logos -> nome da org ->
// metadados + respostas). Reutilizada pelo PDF individual e pelo combinado.
async function buildHistoricSection(
  historic: Historic,
  form: Form,
  locale: string,
  columnCount: number,
  bodyFontSize: number,
  customLogo: string | null,
  orgName: string,
  pageBreakBefore = false,
): Promise<any[]> {
  const items = await formatHistoricToItems(historic, form.fields, locale);
  const content = generatePdfJson(
    items,
    form.fields,
    columnCount,
    bodyFontSize,
  );

  const header: any = buildLogoHeader(customLogo);
  if (pageBreakBefore) header.pageBreak = 'before';

  const section: any[] = [header];
  if (orgName) section.push(buildOrgNameBlock(orgName));
  section.push(content);
  return section;
}

export async function generateAnswerPdf(
  historic: Historic,
  form: Form,
  locale: string,
  columnCount = 3,
  logoUrl = '',
  orgName = '',
  fileName = '',
) {
  setupPdfFonts();
  const { bodyFontSize, titleFontSize } = calcFontSizes(columnCount);
  const customLogo = logoUrl ? await getBase64FromImageUrl(logoUrl) : null;

  const section = await buildHistoricSection(
    historic,
    form,
    locale,
    columnCount,
    bodyFontSize,
    customLogo,
    orgName,
  );

  const docDefinitions = buildDocDefinitions(
    section,
    titleFontSize,
    bodyFontSize,
  );

  const name =
    fileName ||
    buildIndividualPdfName({
      formName: historic.form_name,
      formDate: historic.form_date,
    });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  pdfMake.createPdf(docDefinitions).download(ensurePdfExtension(name));
}

// Combinado: varias anotacoes num unico documento, ordenadas por data de
// registro ascendente, cada uma comecando em pagina nova.
export async function generateCombinedAnswerPdf(
  historics: Historic[],
  form: Form,
  locale: string,
  columnCount = 3,
  logoUrl = '',
  orgName = '',
  fileName = '',
) {
  setupPdfFonts();
  const { bodyFontSize, titleFontSize } = calcFontSizes(columnCount);
  const customLogo = logoUrl ? await getBase64FromImageUrl(logoUrl) : null;

  const sorted = [...historics].sort((a, b) => a.form_date - b.form_date);

  const content: any[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const section = await buildHistoricSection(
      sorted[i],
      form,
      locale,
      columnCount,
      bodyFontSize,
      customLogo,
      orgName,
      i > 0,
    );
    content.push(...section);
  }

  const docDefinitions = buildDocDefinitions(
    content,
    titleFontSize,
    bodyFontSize,
  );

  const name = fileName || buildCombinedPdfName({ orgName });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  pdfMake.createPdf(docDefinitions).download(ensurePdfExtension(name));
}
